// frontend/src/pages/Sabesp/components/SourcesPanel.js
import React, { useEffect, useState } from "react";
import { figureThumbnailUrl, authHeader } from "../api/sabespClient.js";

// <img src> nao consegue mandar header de Authorization -- por isso a
// miniatura e buscada via fetch (com o JWT) e convertida pra blob URL, em
// vez de apontar direto pra figureThumbnailUrl().
// Miniaturas ja baixadas nesta sessao (figureId -> blob URL): trocar de conversa ou reabrir a mensagem nao baixa de novo.
const thumbCache = new Map();

function FigureThumb({ figureId, token, alt }) {
  const [src, setSrc] = useState(() => thumbCache.get(figureId) || null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setFailed(false);
    if (thumbCache.has(figureId)) {
      setSrc(thumbCache.get(figureId));
      return undefined;
    }
    setSrc(null);

    fetch(figureThumbnailUrl(figureId), { headers: authHeader(token) })
      .then((resp) => {
        if (!resp.ok) throw new Error("Falha ao carregar miniatura");
        return resp.blob();
      })
      .then((blob) => {
        if (cancelled) return;
        const objectUrl = URL.createObjectURL(blob);
        thumbCache.set(figureId, objectUrl);
        setSrc(objectUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
    };
  }, [figureId, token]);

  if (failed) return null;
  return (
    <>
      {!src && <span className="sabesp-spinner sabesp-thumb-spinner" aria-label="Carregando figura" />}
      <img src={src || undefined} alt={alt} style={{ display: src ? "block" : "none" }} />
    </>
  );
}

function EvidenceToggle({ open, onClick, label }) {
  return (
    <button
      type="button"
      className="sabesp-evidence-toggle"
      onClick={onClick}
      aria-label={label}
      aria-expanded={open}
    >
      <svg
        className={`sabesp-evidence-chevron${open ? " open" : ""}`}
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>
  );
}

export default function SourcesPanel({ citations, figures, token, onOpenCitation, onOpenFigure }) {
  const hasCitations = Array.isArray(citations) && citations.length > 0;
  const hasFigures = Array.isArray(figures) && figures.length > 0;
  const [citationsOpen, setCitationsOpen] = useState(true);
  const [figuresOpen, setFiguresOpen] = useState(true);
  if (!hasCitations && !hasFigures) return null;

  return (
    <div className="sabesp-evidence-block">
      {hasCitations && (
        <div>
          <div className="sabesp-evidence-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
              <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
            <span>Fontes</span>
            <EvidenceToggle
              open={citationsOpen}
              onClick={() => setCitationsOpen((v) => !v)}
              label={citationsOpen ? "Recolher fontes" : "Expandir fontes"}
            />
            <span className="sabesp-evidence-count">{citations.length}</span>
          </div>
          <div className={`sabesp-sources-panel sabesp-collapsible${citationsOpen ? " open" : ""}`}>
            {citations.map((c, i) => {
              const trecho = (c.quoted_text || "").slice(0, 280);
              const reticencias = (c.quoted_text || "").length > 280 ? "…" : "";
              return (
                <article className="sabesp-source-item" key={c.citation_id}>
                  <div className="sabesp-source-top">
                    <span className="sabesp-source-num">{i + 1}</span>
                    <div className="sabesp-source-meta">
                      <strong title={c.document_name || ""}>{c.document_name || "Documento"}</strong>
                      <span>
                        Página {c.page_number}
                        {c.section_title ? ` · ${c.section_title}` : ""}
                      </span>
                    </div>
                  </div>
                  <p className="sabesp-source-quote">
                    “{trecho}
                    {reticencias}”
                  </p>
                  <button
                    className="sabesp-source-open"
                    onClick={() => onOpenCitation(c.document_id, c.page_number, c.citation_id)}
                  >
                    Abrir no documento
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                      <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      )}

      {hasFigures && (
        <div>
          <div className="sabesp-evidence-title">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <circle cx="8.5" cy="8.5" r="1.5"></circle>
              <polyline points="21 15 16 10 5 21"></polyline>
            </svg>
            <span>Figuras relacionadas</span>
            <EvidenceToggle
              open={figuresOpen}
              onClick={() => setFiguresOpen((v) => !v)}
              label={figuresOpen ? "Recolher figuras" : "Expandir figuras"}
            />
            <span className="sabesp-evidence-count">{figures.length}</span>
          </div>
          <div className={`sabesp-figures-grid sabesp-collapsible${figuresOpen ? " open" : ""}`}>
            {figures.map((f) => (
              <article
                className="sabesp-figure-card"
                key={f.figure_id}
                onClick={() => onOpenFigure(f.document_id, f.page_number, f.figure_id)}
              >
                <div className="sabesp-figure-thumb">
                  <FigureThumb figureId={f.figure_id} token={token} alt={f.description || f.title || "Figura"} />
                </div>
                <div className="sabesp-figure-info">
                  <span className="sabesp-figure-type">{(f.figure_type || "").replace(/_/g, " ")}</span>
                  <h4 title={f.title || ""}>{f.title || "Figura"}</h4>
                  <p>
                    {f.document_name || ""} · página {f.page_number}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
